import React from "react";
import { fireEvent, render, screen, waitFor } from "@testing-library/react";
import { buildEditorColumn } from "./editors";
import "@testing-library/jest-dom";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import type { GridCellParams, GridColDef } from "@mui/x-data-grid";
import { applyColumnDisplay, readColumnDisplay } from "./columnDisplay";

const cell = (column: GridColDef, value: unknown) =>
  (column.renderCell as (params: GridCellParams) => React.ReactElement)({
    value,
  } as GridCellParams);

describe("readColumnDisplay", () => {
  it("reads camelCase and snake_case status colours, dropping unknown colours", () => {
    expect(
      readColumnDisplay({
        columnStatusColors: {
          Status: { Approved: "success", Bad: "rainbow" },
        },
      }),
    ).toEqual({ Status: { statusColors: { Approved: "success" } } });
    expect(
      readColumnDisplay({
        column_status_colors: { Stage: { Open: "warning" } },
      }),
    ).toEqual({ Stage: { statusColors: { Open: "warning" } } });
  });

  it("returns nothing when no column is configured or every colour is unknown", () => {
    expect(readColumnDisplay({})).toBeUndefined();
    expect(readColumnDisplay({ columnStatusColors: [] })).toBeUndefined();
    expect(
      readColumnDisplay({ columnStatusColors: { Status: { A: "rainbow" } } }),
    ).toBeUndefined();
  });
});

describe("readColumnDisplay avatars", () => {
  it("reads avatar columns from camelCase or snake_case, ignoring non-strings", () => {
    expect(
      readColumnDisplay({ avatarColumns: ["Name", 7, "", "Reviewer"] }),
    ).toEqual({ Name: { avatar: true }, Reviewer: { avatar: true } });
    expect(readColumnDisplay({ avatar_columns: ["Name"] })).toEqual({
      Name: { avatar: true },
    });
  });

  it("combines an avatar column and status colours without losing either", () => {
    expect(
      readColumnDisplay({
        avatarColumns: ["Name"],
        columnStatusColors: { Status: { Open: "warning" } },
      }),
    ).toEqual({
      Name: { avatar: true },
      Status: { statusColors: { Open: "warning" } },
    });
  });
});

describe("applyColumnDisplay", () => {
  const status: GridColDef = { field: "Status", editable: false };
  const name: GridColDef = { field: "Name", editable: false };

  it("renders a configured read-only column as a coloured status pill", () => {
    const columns = applyColumnDisplay([status, name], {
      Status: { statusColors: { Approved: "success", Pending: "warning" } },
    });

    render(
      <>
        {cell(columns[0], "Approved")}
        {cell(columns[0], "pending")}
        {cell(columns[0], "Unknown value")}
      </>,
    );

    const chips = screen.getAllByTestId("status-chip");
    expect(chips.map((chip) => chip.dataset.statusColor)).toEqual([
      "success",
      "warning", // case-insensitive, like every other status pill
      "neutral", // a value with no configured colour
    ]);
    expect(chips[0]).toHaveTextContent("Approved");
  });

  it("leaves a column with no setting, and a setting-free grid, exactly as they were", () => {
    const columns = [status, name];
    const applied = applyColumnDisplay(columns, {
      Status: { statusColors: { Approved: "success" } },
    });

    expect(applied[1]).toBe(name); // same object: no renderCell added
    expect(applyColumnDisplay(columns, undefined)).toBe(columns);
  });

  it("keeps the editor of an editable column", () => {
    const renderEditCell = jest.fn(() => (
      <input aria-label="Existing editor" />
    ));
    const editable: GridColDef = {
      field: "Status",
      editable: true,
      renderEditCell,
    };

    const [column] = applyColumnDisplay([editable], {
      Status: { statusColors: { Approved: "success" } },
    });

    expect(column.editable).toBe(true);
    render(<>{cell(column, "Approved")}</>);
    expect(screen.getByTestId("status-chip")).toHaveTextContent("Approved");
  });

  it("renders nothing for an empty cell", () => {
    const [column] = applyColumnDisplay([status], {
      Status: { statusColors: { Approved: "success" } },
    });

    render(<>{cell(column, null)}</>);

    expect(screen.queryByTestId("status-chip")).not.toBeInTheDocument();
  });

  it("renders a configured read-only person-name column as initials plus the name", () => {
    const [column] = applyColumnDisplay([name], { Name: { avatar: true } });

    render(<>{cell(column, "Ada Lovelace")}</>);

    expect(screen.getByTestId("initials-avatar")).toHaveTextContent("AL");
    expect(screen.getByText("Ada Lovelace")).toBeInTheDocument();
  });

  it("keeps the editor of an editable person-name column", () => {
    const editable: GridColDef = { field: "Name", editable: true };

    const [column] = applyColumnDisplay([editable], { Name: { avatar: true } });

    expect(column).toBe(editable);
  });
});

describe("configured cells inside the grid's real provider tree", () => {
  // ViewGridNew renders its DataGrid inside a Material UI ThemeProvider that has no Joy theme. A
  // Joy component in a cell throws there, so these tests render the cells exactly that way.
  const materialOnly = createTheme({ palette: { mode: "light" } });

  it("renders a status pill and an avatar under a Material-only theme provider", () => {
    const columns = applyColumnDisplay(
      [
        { field: "Status", editable: false },
        { field: "Name", editable: false },
      ],
      {
        Status: { statusColors: { Approved: "success" } },
        Name: { avatar: true },
      },
    );

    render(
      <ThemeProvider theme={materialOnly}>
        {cell(columns[0], "Approved")}
        {cell(columns[1], "Ada Lovelace")}
      </ThemeProvider>,
    );

    const chip = screen.getByTestId("status-chip");
    expect(chip).toHaveTextContent("Approved");
    // (The colours are Joy CSS variables; jsdom cannot hold var() values, a browser check does.)
    expect(chip).toHaveAttribute("data-status-color", "success");
    expect(screen.getByTestId("initials-avatar")).toHaveTextContent("AL");
  });

  it("renders the configured cells in a real DataGrid under that provider", () => {
    const columns = applyColumnDisplay(
      [
        { field: "Name", headerName: "Name", editable: false, width: 200 },
        { field: "Status", headerName: "Status", editable: false, width: 160 },
      ],
      {
        Status: { statusColors: { Approved: "success", Pending: "warning" } },
        Name: { avatar: true },
      },
    );

    render(
      <ThemeProvider theme={materialOnly}>
        <DataGrid
          autoHeight
          disableVirtualization
          hideFooter
          columns={columns}
          rows={[
            { id: 1, Name: "Ada Lovelace", Status: "Approved" },
            { id: 2, Name: "Grace Hopper", Status: "Pending" },
          ]}
        />
      </ThemeProvider>,
    );

    expect(
      screen
        .getAllByTestId("status-chip")
        .map((chip) => chip.dataset.statusColor),
    ).toEqual(["success", "warning"]);
    expect(
      screen.getAllByTestId("initials-avatar").map((a) => a.textContent),
    ).toEqual(["AL", "GH"]);
  });
});

describe("column widths", () => {
  it("accepts flexible columns while an explicit width remains authoritative", () => {
    const display = readColumnDisplay({
      columnFlex: { Name: 2, Status: 1 },
      columnWidths: { Status: 120 },
    });
    const columns = applyColumnDisplay(
      [
        { field: "Name", width: 150 },
        { field: "Status", width: 150, flex: 3 },
      ],
      display,
    );
    expect(columns[0].flex).toBe(2);
    expect(columns[0].minWidth).toBeGreaterThan(0);
    expect(columns[1].width).toBe(120);
    expect(columns[1].flex).toBe(0);
    expect(
      readColumnDisplay({
        column_flex: { Name: 1, Bad: -1, Missing: "auto", Infinite: Infinity },
      }),
    ).toEqual({ Name: { flex: 1 } });
  });
  it("reads per-column widths from camelCase or snake_case", () => {
    expect(readColumnDisplay({ columnWidths: { Name: 240 } })).toEqual({
      Name: { width: 240 },
    });
    expect(readColumnDisplay({ column_widths: { Status: 90 } })).toEqual({
      Status: { width: 90 },
    });
  });

  it("drops non-numeric, non-positive and non-finite widths and clamps the rest", () => {
    expect(
      readColumnDisplay({
        columnWidths: {
          A: "wide",
          B: 0,
          C: -10,
          D: Infinity,
          E: 5,
          F: 99999,
          G: 180.4,
        },
      }),
    ).toEqual({ E: { width: 40 }, F: { width: 1000 }, G: { width: 180 } });
    expect(readColumnDisplay({ columnWidths: [] })).toBeUndefined();
  });

  it("combines a width with status colours and avatars on the same column", () => {
    expect(
      readColumnDisplay({
        avatarColumns: ["Name"],
        columnWidths: { Name: 260 },
      }),
    ).toEqual({ Name: { avatar: true, width: 260 } });
  });

  it("sets the width of a configured column, editable or not, and leaves the others at their own width", () => {
    const columns = applyColumnDisplay(
      [
        { field: "Name", editable: true, width: 150 },
        { field: "Role", editable: false, width: 150 },
        { field: "Other", editable: false, width: 150 },
      ],
      { Name: { width: 260 }, Role: { width: 90 } },
    );
    expect(columns.map((column) => column.width)).toEqual([260, 90, 150]);
    // A width alone adds no renderer.
    expect(columns[0].renderCell).toBeUndefined();
  });

  it("keeps a configured width together with a status pill", () => {
    const [column] = applyColumnDisplay(
      [{ field: "Status", editable: false }],
      {
        Status: { statusColors: { Open: "warning" }, width: 120 },
      },
    );
    expect(column.width).toBe(120);
    expect(column.renderCell).toBeDefined();
  });
});

it("shows editable enum status colours in the real grid and commits the existing selected value", async () => {
  const update = jest.fn((row) => row);
  const editor = buildEditorColumn({
    field: "Status",
    inputType: "select",
    enumOptions: ["Pending", "Approved"],
    nullable: false,
  } as any);
  const columns = applyColumnDisplay(
    [
      {
        field: "Status",
        headerName: "Status",
        width: 200,
        editable: true,
        ...editor,
      },
    ],
    {
      Status: { statusColors: { Pending: "warning", Approved: "success" } },
    },
  );
  render(
    <ThemeProvider theme={createTheme()}>
      <DataGrid
        autoHeight
        disableVirtualization
        hideFooter
        rows={[{ id: 1, Status: "Pending" }]}
        columns={columns}
        processRowUpdate={update}
      />
    </ThemeProvider>,
  );
  expect(screen.getByTestId("status-chip")).toHaveAttribute(
    "data-status-color",
    "warning",
  );
  fireEvent.doubleClick(screen.getByRole("gridcell", { name: "Pending" }));
  fireEvent.mouseDown(await screen.findByRole("combobox"));
  const approved = await screen.findByRole("option", { name: "Approved" });
  expect(
    // Inspect the rendered pill attribute within the option.
    // eslint-disable-next-line testing-library/no-node-access
    approved.querySelector('[data-status-color="success"]'),
  ).not.toBeNull();
  fireEvent.click(approved);
  await waitFor(() =>
    expect(update).toHaveBeenCalledWith(
      expect.objectContaining({ id: 1, Status: "Approved" }),
      expect.anything(),
      expect.anything(),
    ),
  );
});

describe("optional column header text", () => {
  it("reads only non-empty headerName overrides from the column display map", () => {
    expect(
      readColumnDisplay({
        columnDisplay: {
          first_name: { headerName: "Name" },
          empty: { headerName: " " },
          number: { headerName: 3 },
        },
      }),
    ).toEqual({ first_name: { headerName: "Name" } });
    expect(
      readColumnDisplay({
        column_display: { first_name: { headerName: "Name" } },
      }),
    ).toEqual({ first_name: { headerName: "Name" } });
  });
  it("changes only header text, preserving field, editors, sorting and filtering", () => {
    const renderEditCell = jest.fn();
    const valueGetter = jest.fn();
    const sortComparator = jest.fn();
    const filterOperators = [
      { label: "equals", value: "equals", getApplyFilterFn: jest.fn() },
    ];
    const columns: GridColDef[] = [
      {
        field: "first_name",
        headerName: "First name",
        editable: true,
        renderEditCell,
        valueGetter,
        sortComparator,
        filterOperators,
      },
    ];
    const result = applyColumnDisplay(
      columns,
      readColumnDisplay({
        columnDisplay: { first_name: { headerName: "Name" } },
      }),
    );
    expect(result[0]).toEqual({ ...columns[0], headerName: "Name" });
    expect(columns[0].headerName).toBe("First name");
  });
  it("renders the override alongside a composed name without changing editability", () => {
    const columns = applyColumnDisplay(
      [{ field: "first_name", headerName: "First name", editable: true }],
      readColumnDisplay({
        columnDisplay: { first_name: { headerName: "Name" } },
        columnRecordLabelTemplates: { first_name: "{first_name} {last_name}" },
      }),
    );
    render(
      <ThemeProvider theme={createTheme()}>
        <div style={{ height: 300, width: 600 }}>
          <DataGrid
            rows={[{ id: 1, first_name: "Alex", last_name: "Example" }]}
            columns={columns}
          />
        </div>
      </ThemeProvider>,
    );
    expect(
      screen.getByRole("columnheader", { name: /Name/ }),
    ).toBeInTheDocument();
    expect(screen.getByText("Alex Example")).toBeInTheDocument();
    expect(columns[0].editable).toBe(true);
  });
});

it("keeps a header override when the same column has an explicit width or flex", () => {
  const base: GridColDef = { field: "first_name", headerName: "First name" };
  expect(
    applyColumnDisplay(
      [base],
      readColumnDisplay({
        columnDisplay: { first_name: { headerName: "Name" } },
        columnWidths: { first_name: 240 },
        columnFlex: { first_name: 2 },
      }),
    )[0],
  ).toMatchObject({
    field: "first_name",
    headerName: "Name",
    width: 240,
    flex: 0,
  });
  expect(
    applyColumnDisplay(
      [base],
      readColumnDisplay({
        columnDisplay: { first_name: { headerName: "Name" } },
        columnFlex: { first_name: 2 },
      }),
    )[0],
  ).toMatchObject({ field: "first_name", headerName: "Name", flex: 2 });
});
